import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MenuIcon, XIcon } from 'lucide-react';
interface NavbarProps {
  onOpenBooking: () => void;
}
const navLinks = [{
  label: 'About',
  href: '#about'
}, {
  label: 'Services',
  href: '#services'
}, {
  label: 'Experience',
  href: '#experience'
}, {
  label: 'Gallery',
  href: '#gallery'
}, {
  label: 'Contact',
  href: '#location'
}];
export function Navbar({
  onOpenBooking
}: NavbarProps) {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener('scroll', handleScroll, {
      passive: true
    });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  useEffect(() => {
    if (mobileOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [mobileOpen]);
  const handleNavClick = (href: string) => {
    setMobileOpen(false);
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({
      behavior: 'smooth'
    });
  };
  return <motion.nav className="fixed top-0 left-0 right-0 z-50 transition-colors duration-500" animate={{
    backgroundColor: scrolled ? 'rgba(250, 247, 242, 0.92)' : 'rgba(250, 247, 242, 0)',
    backdropFilter: scrolled ? 'blur(12px)' : 'blur(0px)'
  }} transition={{
    duration: 0.4
  }} role="navigation" aria-label="Main navigation" data-id="element-233">
      <div className="max-w-7xl mx-auto px-6 lg:px-8" data-id="element-234">
        <div className="flex items-center justify-between h-20" data-id="element-235">
          {/* Logo */}
          <a href="#" onClick={e => {
          e.preventDefault();
          window.scrollTo({
            top: 0,
            behavior: 'smooth'
          });
        }} className={`font-heading text-xl md:text-2xl font-semibold tracking-[0.25em] select-none transition-colors duration-400 ${scrolled ? 'text-bark' : 'text-cream drop-shadow-[0_1px_2px_rgba(0,0,0,0.3)]'}`} data-id="element-236">
            ZEN BARN SPA
          </a>

          {/* Desktop links + Book Now */}
          <div className="hidden md:flex items-center gap-8" data-id="element-237">
            {navLinks.map(link => <a key={link.label} href={link.href} onClick={e => {
            e.preventDefault();
            handleNavClick(link.href);
          }} className={`text-sm font-medium tracking-wide transition-colors duration-300 ${scrolled ? 'text-bark-light hover:text-terracotta' : 'text-cream/85 hover:text-white drop-shadow-[0_1px_2px_rgba(0,0,0,0.2)]'}`} data-id="element-238">
                {link.label}
              </a>)}
            <button onClick={onOpenBooking} className={`ml-2 px-6 py-2.5 font-body font-medium text-sm tracking-wide rounded-full transition-colors duration-300 focus-visible:ring-2 focus-visible:ring-terracotta focus-visible:ring-offset-2 ${scrolled ? 'bg-terracotta text-white hover:bg-terracotta-dark' : 'bg-white/20 text-white backdrop-blur-sm border border-white/30 hover:bg-white/30'}`} data-id="element-239">
              Book Now
            </button>
          </div>

          {/* Mobile toggle */}
          <button className={`md:hidden p-2 transition-colors duration-300 ${scrolled ? 'text-bark' : 'text-cream drop-shadow-[0_1px_2px_rgba(0,0,0,0.3)]'}`} onClick={() => setMobileOpen(!mobileOpen)} aria-label={mobileOpen ? 'Close menu' : 'Open menu'} aria-expanded={mobileOpen} data-id="element-240">
            {mobileOpen ? <XIcon className="w-6 h-6" data-id="element-241" /> : <MenuIcon className="w-6 h-6" data-id="element-242" />}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      <AnimatePresence data-id="element-243">
        {mobileOpen && <motion.div initial={{
        opacity: 0,
        x: '100%'
      }} animate={{
        opacity: 1,
        x: 0
      }} exit={{
        opacity: 0,
        x: '100%'
      }} transition={{
        type: 'tween',
        duration: 0.35,
        ease: 'easeInOut'
      }} className="fixed inset-0 top-0 bg-cream z-40 flex flex-col items-center justify-center gap-8 md:hidden" data-id="element-244">
            <button className="absolute top-6 right-6 p-2 text-bark" onClick={() => setMobileOpen(false)} aria-label="Close menu" data-id="element-245">
              <XIcon className="w-7 h-7" data-id="element-246" />
            </button>
            {navLinks.map((link, i) => <motion.a key={link.label} href={link.href} onClick={e => {
          e.preventDefault();
          handleNavClick(link.href);
        }} initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.1 + i * 0.06
        }} className="font-heading text-3xl font-medium text-bark hover:text-terracotta transition-colors" data-id="element-247">
                {link.label}
              </motion.a>)}
            <motion.button initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.1 + navLinks.length * 0.06
        }} onClick={() => {
          setMobileOpen(false);
          onOpenBooking();
        }} className="mt-4 px-10 py-4 bg-terracotta text-white font-body font-medium text-lg tracking-wide rounded-full hover:bg-terracotta-dark transition-colors duration-300" data-id="element-248">
              Book Now
            </motion.button>
          </motion.div>}
      </AnimatePresence>
    </motion.nav>;
}